import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useTheme } from '../../context/ThemeContext'
import NotificationBell from '../../components/NotificationBell'
import { useState, useEffect, lazy, Suspense } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import AdminSidebar from './AdminSidebar'
import {
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Clock,
  TrendingUp,
  CheckCircle,
  BookOpen,
  UserPlus,
  BookPlus,
  BarChart,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

// ─── Lazy-loaded admin pages (code-split per route) ─────────────────────────
const Students = lazy(() => import('./Students'))
const CoursesEnhanced = lazy(() => import('./CoursesEnhanced'))
const CourseDetails = lazy(() => import('./CourseDetails'))
const SettingsPage = lazy(() => import('./Settings'))
const Finance = lazy(() => import('./Finance'))
const Trainers = lazy(() => import('./Trainers'))
const Equipment = lazy(() => import('./Equipment'))
const Reports = lazy(() => import('./Reports'))
const AIReportsPage = lazy(() => import('./AIReportsPage'))
const Attendance = lazy(() => import('./Attendance'))
const SelfAttendanceQueue = lazy(() => import('./SelfAttendanceQueue'))
const Communications = lazy(() => import('./Communications'))
const CommunicationOps = lazy(() => import('./CommunicationOps'))
const UserManagement = lazy(() => import('./UserManagement'))
const Assignments = lazy(() => import('./Assignments'))
const ContentManagement = lazy(() => import('./ContentManagement'))
const Resources = lazy(() => import('./Resources'))
const SystemLogs = lazy(() => import('./SystemLogs'))
const CalendarSchedule = lazy(() => import('./CalendarSchedule'))
const CertificatesManagement = lazy(() => import('./CertificatesManagement'))
const PendingRegistrations = lazy(() => import('./PendingRegistrations'))
const AdmissionsCommandCenter = lazy(() => import('./AdmissionsCommandCenter'))
const WebsiteAnalytics = lazy(() => import('./WebsiteAnalytics'))
const Intakes = lazy(() => import('./Intakes'))
const AdminProfile = lazy(() => import('./Profile'))
const MasterclassAdminPage = lazy(() => import('./MasterclassAdminPage'))
const ProjectLabAdminPage = lazy(() => import('./ProjectLab'))
const CreativeToolsAdminPage = lazy(() => import('./CreativeToolsAdmin'))
const MasterclassMediaManager = lazy(() => import('./MasterclassMediaManager'))
const AITutorDashboard = lazy(() => import('./AITutorDashboard'))
const AcademicMonitor = lazy(() => import('./AcademicMonitor'))
const AIAdminPage = lazy(() => import('./AIAdminPage'))
const AIGovernancePage = lazy(() => import('./AIGovernancePage'))
const AIActionCenterPage = lazy(() => import('./AIActionCenterPage'))
const AICaseManagementPage = lazy(() => import('./AICaseManagementPage'))
const AICommunicationCenterPage = lazy(() => import('./AICommunicationCenterPage'))
const AIIntelligenceCenterPage = lazy(() => import('./AIIntelligenceCenterPage'))
const AIKnowledgeCenterPage = lazy(() => import('./AIKnowledgeCenterPage'))
const AIWorkflowCenterPage = lazy(() => import('./AIWorkflowCenterPage'))
const AIPerformanceOutcomesPage = lazy(() => import('./AIPerformanceOutcomesPage'))
const AIPersonalizationCenterPage = lazy(() => import('./AIPersonalizationCenterPage'))
const AIForecastingCenterPage = lazy(() => import('./AIForecastingCenterPage'))
const AIEngagementCenterPage = lazy(() => import('./AIEngagementCenterPage'))
const AIAutonomyControlCenterPage = lazy(() => import('./AIAutonomyControlCenterPage'))
const AIGovernanceAssurancePage = lazy(() => import('./AIGovernanceAssurancePage'))
const AIAcademyOSPage = lazy(() => import('./AIAcademyOSPage'))
const SocialMediaStudioPage = lazy(() => import('./SocialMediaStudioPage'))
const DigitalLab = lazy(() => import('./DigitalLab'))
const DigitalLabLayout = lazy(() => import('./digital-lab/DigitalLabLayout'))
const DigitalLabDashboard = lazy(() => import('./digital-lab/DigitalLabDashboard'))
const DigitalLabComputers = lazy(() => import('./digital-lab/DigitalLabComputers'))
const DigitalLabComputerDetail = lazy(() => import('./digital-lab/DigitalLabComputerDetail'))
const DigitalLabGroups = lazy(() => import('./digital-lab/DigitalLabGroups'))
const DigitalLabApplications = lazy(() => import('./digital-lab/DigitalLabApplications'))
const DigitalLabActivity = lazy(() => import('./digital-lab/DigitalLabActivity'))
const DigitalLabAuditLog = lazy(() => import('./digital-lab/DigitalLabAuditLog'))
const DigitalLabAlerts = lazy(() => import('./digital-lab/DigitalLabAlerts'))
const DigitalLabPolicies = lazy(() => import('./digital-lab/DigitalLabPolicies'))
const DigitalLabUsers = lazy(() => import('./digital-lab/DigitalLabUsers'))
const DigitalLabAIAssistant = lazy(() => import('./digital-lab/DigitalLabAIAssistant'))
const DigitalLabWorkspaces = lazy(() => import('./digital-lab/DigitalLabWorkspaces'))
const DigitalLabSettings = lazy(() => import('./digital-lab/DigitalLabSettings'))
const DigitalLabComingSoon = lazy(() => import('./digital-lab/DigitalLabComingSoon'))
const Receipt = lazy(() => import('../student/Receipt'))
import DailyAcademyBriefing from './DailyAcademyBriefing'

function DashboardHome() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recentActivity, setRecentActivity] = useState([])

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const { data } = await api.get('/admin/dashboard/stats')
      setStats(data)
      setLoading(false)
    } catch (error) {
      console.error('Dashboard data error:', error)
      toast.error('Failed to load dashboard data')
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-10">
      <div className="page-header">
        <h1 
          className="text-2xl sm:text-3xl text-gray-900 dark:text-white"
          style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
        >
          Admin Dashboard
        </h1>
        <button onClick={fetchDashboardData} className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
          <Clock size={18} />
          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 500 }}>Refresh</span>
        </button>
      </div>

      {/* AI-009 Daily Academy Briefing */}
      <DailyAcademyBriefing />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Students</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.totalStudents || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
            <TrendingUp size={14} className="text-green-500" />
            +{stats?.newStudentsThisMonth || 0} this month
          </p>
        </div>

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Trainers</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.totalTrainers || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
            <CheckCircle size={14} className="text-green-500" />
            {stats?.activeTrainers || 0} active
          </p>
        </div>

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Active Courses</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.activeCourses || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
            <BookOpen size={14} className="text-purple-500" />
            {stats?.totalEnrollments || 0} enrollments
          </p>
        </div>

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Revenue</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            UGX {stats?.totalRevenue?.toLocaleString() || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
            <TrendingUp size={14} className="text-green-500" />
            UGX {stats?.revenueThisMonth?.toLocaleString() || 0} this month
          </p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 pt-6 border-t border-gray-200 dark:border-gray-800">
        {stats?.pendingRegistrations > 0 && (
          <Link to="/admin/pending-registrations" className="stat-item group">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium group-hover:text-yellow-600 dark:group-hover:text-yellow-500 transition-colors">Pending Registrations</span>
            <p className="text-3xl text-yellow-600 dark:text-yellow-500 font-display font-semibold mt-1">
              {stats.pendingRegistrations}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Awaiting your approval</p>
          </Link>
        )}

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Pending Payments</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.pendingPayments || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            UGX {stats?.pendingPaymentsAmount?.toLocaleString() || 0}
          </p>
        </div>

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Equipment Bookings</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.activeBookings || 0}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stats?.pendingBookings || 0} pending approval</p>
        </div>

        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Attendance Rate</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">
            {stats?.attendanceRate || 0}%
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Based on last 30 days</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
        <div className="section-header">
          <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
        </div>
        <div className="space-y-0">
          {recentActivity.map((activity, index) => (
            <div key={index} className="data-row">
              <div className={`p-2 rounded-full ${
                activity.type === 'student' ? 'bg-blue-500/10 text-blue-500' :
                activity.type === 'course' ? 'bg-purple-500/10 text-purple-500' :
                activity.type === 'payment' ? 'bg-green-500/10 text-green-500' :
                'bg-orange-500/10 text-orange-500'
              }`}>
                <activity.icon size={18} />
              </div>
              <div className="flex-1">
                <p className="text-gray-900 dark:text-white text-sm font-sans font-medium">
                  {activity.action}
                </p>
                <p className="text-gray-500 dark:text-gray-500 text-xs font-sans font-light">
                  {activity.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
        <div className="section-header">
          <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <Link to="/admin/students" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-sans font-medium transition-colors">
            <UserPlus size={18} />
            Add Student
          </Link>
          <Link to="/admin/trainers" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-sans font-medium transition-colors">
            <UserPlus size={18} />
            Add Trainer
          </Link>
          <Link to="/admin/courses" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-sans font-medium transition-colors">
            <BookPlus size={18} />
            Create Course
          </Link>
          <Link to="/admin/reports" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-sans font-medium transition-colors">
            <BarChart size={18} />
            View Reports
          </Link>
        </div>
      </div>
    </div>
  )
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { darkMode, toggleDarkMode } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  return (
    <div className={`h-screen flex flex-col bg-gray-50 dark:bg-cinema-black ${darkMode ? '' : 'admin-light'}`}>
      <nav className="border-b border-gray-200 dark:border-white/10 sticky top-0 z-30 bg-white dark:bg-cinema-charcoal">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="mr-3 p-2 lg:hidden text-gray-600 dark:text-cinema-soft hover:text-primary-600 dark:hover:text-cinema-gold hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <img
                src={darkMode ? '/Logos/SMA LOGO  DARK MODE.png' : '/Logos/SMA LOGO  VERSION 2-01.png'}
                alt="Synergy Multimedia Academy Kampala - SMA Logo - Admin Portal Dashboard"
                className="h-10"
              />
              <span className="ml-3 text-xl text-gray-900 dark:text-white hidden sm:block font-display font-bold">
                Admin Portal
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link
                to="profile"
                className="hidden sm:block text-sm text-gray-600 dark:text-cinema-soft hover:text-primary-600 dark:hover:text-cinema-gold transition-colors font-sans"
                title="Edit profile"
              >
                {user?.first_name} {user?.last_name}
              </Link>
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-600 dark:text-cinema-soft hover:text-primary-600 dark:hover:text-cinema-gold hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <NotificationBell darkMode={darkMode} />
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-cinema-soft hover:text-primary-600 dark:hover:text-cinema-gold hover:border-primary-500/50 dark:hover:border-cinema-gold/50 transition-colors font-sans">
                <LogOut size={18} />
                <span className="hidden sm:inline font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <AdminSidebar sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

          <main className="flex-1 min-w-0 overflow-y-auto">
            <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="dashboard" element={<DashboardHome />} />
              <Route path="admissions" element={<AdmissionsCommandCenter />} />
              <Route path="intakes" element={<Intakes />} />
              <Route path="website-analytics" element={<WebsiteAnalytics />} />
              <Route path="ai-tutor" element={<AITutorDashboard />} />
              <Route path="academic-monitor" element={<AcademicMonitor />} />
              <Route path="ai-admin" element={<AIAdminPage />} />
              <Route path="ai-actions" element={<AIActionCenterPage />} />
              <Route path="ai-cases" element={<AICaseManagementPage />} />
              <Route path="ai-communications" element={<AICommunicationCenterPage />} />
              <Route path="ai-intelligence" element={<AIIntelligenceCenterPage />} />
              <Route path="ai-knowledge" element={<AIKnowledgeCenterPage />} />
              <Route path="ai-workflows" element={<AIWorkflowCenterPage />} />
              <Route path="ai-outcomes" element={<AIPerformanceOutcomesPage />} />
              <Route path="ai-personalization" element={<AIPersonalizationCenterPage />} />
              <Route path="ai-forecasting" element={<AIForecastingCenterPage />} />
              <Route path="ai-engagement" element={<AIEngagementCenterPage />} />
              <Route path="ai-autonomy" element={<AIAutonomyControlCenterPage />} />
              <Route path="ai-assurance" element={<AIGovernanceAssurancePage />} />
              <Route path="ai-os" element={<AIAcademyOSPage />} />
              <Route path="ai-governance" element={<AIGovernancePage />} />
              <Route path="pending-registrations" element={<PendingRegistrations />} />
              <Route path="students" element={<Students />} />
              <Route path="trainers" element={<Trainers />} />
              <Route path="courses" element={<CoursesEnhanced />} />
              <Route path="courses/:courseId" element={<CourseDetails />} />
              <Route path="masterclass" element={<MasterclassAdminPage />} />
              <Route path="project-lab" element={<ProjectLabAdminPage />} />
              <Route path="creative-tools" element={<CreativeToolsAdminPage />} />
              <Route path="finance" element={<Finance />} />
              <Route path="finance/receipts/:paymentId" element={<Receipt />} />
              <Route path="receipts/:paymentId" element={<Receipt />} />
              <Route path="equipment" element={<Equipment />} />
              <Route path="digital-lab" element={<DigitalLabLayout />}>
                <Route index element={<DigitalLabDashboard />} />
                <Route path="dashboard" element={<DigitalLabDashboard />} />
                <Route path="computers" element={<DigitalLabComputers />} />
                <Route path="computers/:id" element={<DigitalLabComputerDetail />} />
                <Route path="groups" element={<DigitalLabGroups />} />
                <Route path="applications" element={<DigitalLabApplications />} />
                <Route path="activity" element={<DigitalLabActivity />} />
                <Route path="audit-log" element={<DigitalLabAuditLog />} />
                <Route path="users" element={<DigitalLabUsers />} />
                <Route path="workspaces" element={<DigitalLabWorkspaces />} />
                <Route path="policies" element={<DigitalLabPolicies />} />
                <Route path="alerts" element={<DigitalLabAlerts />} />
                <Route path="ai-assistant" element={<DigitalLabAIAssistant />} />
                <Route path="settings" element={<DigitalLabSettings />} />
              </Route>
              <Route path="attendance" element={<Attendance />} />
              <Route path="self-attendance" element={<Navigate to="/admin/attendance?tab=verification" replace />} />
              <Route path="assignments" element={<Assignments />} />
              <Route path="communications" element={<Communications />} />
              <Route path="communications/diagnostics" element={<CommunicationOps />} />
              <Route path="communications/inbox" element={<Navigate to="/admin/communications" replace />} />
              <Route path="communication-ops" element={<Navigate to="/admin/communications/diagnostics" replace />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="content" element={<ContentManagement />} />
              <Route path="social-studio" element={<SocialMediaStudioPage />} />
              <Route path="resources" element={<Resources />} />
              <Route path="calendar" element={<CalendarSchedule />} />
              <Route path="certificates" element={<CertificatesManagement />} />
              <Route path="logs" element={<SystemLogs />} />
              <Route path="reports" element={<Reports />} />
              <Route path="ai-reports" element={<AIReportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="profile" element={<AdminProfile />} />
            </Routes>
            </Suspense>
            </div>
          </main>
      </div>
    </div>
  )
}
